import { useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { C } from "../../shared/theme";
import { goalColorTokens } from "../../shared/goalColors";
import { dueWords } from "../../shared/deadlines";
import { useModalFocus } from "../../shared/useModalFocus";
import { GOAL_VIEWS } from "../tasks/goalOrder";
import { PHASE_LABELS, PHASE_WHEN } from "../today/addToPhase";
import { startHere, rankBadge } from "./ranking";
import { RankStep, useRankOrder } from "./RankStep";
import { StepFrame, stepTitle, stepIntro, primaryButton, quietButton } from "./StepFrame";

// What step 3 says about the goals it offers: only claim "both lists" when every one is ranked on both.
const startHereIntro = (picks, when) => {
  if (!picks.length) return `None of your goals has a task you can do ${when}. Go back to the list and tap "+ New" to add one.`;
  if (picks.every(p => p.urgency != null)) return `These goals are near the top of both lists. Pick one task for ${when}.`;
  return `These are your most important goals with a task you can do ${when}. Pick one.`;
};

// "Help me choose", from Today's add-task sheet: sort your goals by importance, then by urgency, then pick
// a task from the goals near the top of both. With fewer than two goals there is nothing to sort, so it
// opens on the pick. onRank(view, ordered) saves an order; onPick(task) adds the task to this part of the
// day; onSeeAll() goes back to the sheet; onClose() leaves.
export function HelpMeChoose({ phaseId, tasks, goals, onRank, onPick, onSeeAll, onClose }) {
  const active = useMemo(() => (goals || []).filter(g => g.status === "active"), [goals]);
  const [step, setStep] = useState(active.length < 2 ? 3 : 1);
  const [failed, setFailed] = useState(false);
  const importance = useRankOrder(GOAL_VIEWS.IMPORTANCE, active);
  const urgency = useRankOrder(GOAL_VIEWS.URGENCY, active);
  const picks = useMemo(() => startHere(tasks, goals, phaseId), [tasks, goals, phaseId]);
  const containerRef = useRef(null), headingRef = useRef(null);
  useModalFocus({ containerRef, startRef: headingRef, onClose, fallbackSelector: `[data-add-task="${phaseId}"]` });
  const when = PHASE_WHEN[phaseId];

  // Moving on saves the order on screen, so an order accepted without changes counts too.
  const next = (view, rank) => {
    setFailed(false);
    onRank(view, rank.goalsInOrder()).then(() => setStep(step + 1), e => { console.error("Saving goal order failed:", e); setFailed(true); });
  };
  const back = () => (step === 1 || active.length < 2 ? onSeeAll() : setStep(step - 1));
  const footer = step === 1
    ? <button onClick={() => next(GOAL_VIEWS.IMPORTANCE, importance)} style={primaryButton()}>Next: sort by urgency</button>
    : step === 2
      ? <button onClick={() => next(GOAL_VIEWS.URGENCY, urgency)} style={primaryButton()}>Next: pick a task</button>
      : <button onClick={onSeeAll} style={quietButton()}>See all my goals instead</button>;

  return ReactDOM.createPortal(
    <div ref={containerRef} role="dialog" aria-modal="true" aria-label={`Help me choose a task for ${PHASE_LABELS[phaseId]}`}
      className="frame-overlay" style={{ position: "fixed", inset: 0, margin: "0 auto", zIndex: 600, display: "flex", flexDirection: "column" }}>
      <StepFrame name="Help me choose" step={step} of={3} headingRef={headingRef} onClose={onClose}
        onBack={back} backLabel={step === 1 || active.length < 2 ? "Back to the list" : "Back a step"} footer={footer}>
        {step === 1 && <RankStep view={GOAL_VIEWS.IMPORTANCE} rank={importance} failed={failed} setFailed={setFailed} onReorder={onRank} tasks={tasks} />}
        {step === 2 && <RankStep view={GOAL_VIEWS.URGENCY} rank={urgency} failed={failed} setFailed={setFailed} onReorder={onRank} tasks={tasks} />}
        {step === 3 && (
          <div style={{ display: "grid", gap: 14 }}>
            <div>
              <h2 style={stepTitle()}>Here's where to start</h2>
              <p style={stepIntro()}>{startHereIntro(picks, when)}</p>
            </div>
            {picks.map(p => (
              <section key={p.goal.id} style={{ background: C.surface, border: `1px solid ${C.borderCard}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ padding: "12px 14px", borderBottom: `1px solid ${C.border}` }}>
                  <h3 style={{ margin: 0, display: "flex", alignItems: "center", gap: 8, fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>
                    <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: "50%", background: goalColorTokens(p.goal.color).main, flexShrink: 0 }} />
                    {p.goal.title}
                  </h3>
                  <div style={{ marginTop: 4, marginLeft: 17, fontSize: 13, fontWeight: 600, color: C.textMid }}>{rankBadge(p)}</div>
                </div>
                {p.tasks.map((t, n) => {
                  const due = dueWords(t.deadline);
                  return (
                    <button key={t.id} onClick={() => onPick(t)} aria-label={`Add "${t.text}" to ${PHASE_LABELS[phaseId]}`}
                      style={{ width: "100%", minHeight: 52, display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "none", border: "none", borderTop: n ? `1px solid ${C.border}` : "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", fontSize: 15, lineHeight: 1.35, color: C.text }}>{t.text}</span>
                        {due && <span style={{ display: "block", marginTop: 2, fontSize: 13, fontWeight: 600, color: due.overdue ? C.red : C.textMid }}>{due.text}</span>}
                      </span>
                      <span aria-hidden="true" style={{ fontSize: 14, fontWeight: 700, color: C.textGreen }}>Add</span>
                    </button>
                  );
                })}
              </section>
            ))}
          </div>
        )}
      </StepFrame>
    </div>,
    document.body
  );
}
